import React, { useEffect, useState } from "react";
import { Grid, Box, Typography, Container } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import SEO from "../../components/SEO";

interface TeamMember {
  id: number;
  name: string;
  role: string;
  description: string;
  photo_main_url: string;
  photo_hover_url: string;
}

const TeamGrid: React.FC = () => {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`/api/teams?lang=${i18n.language}`)
      .then((res) => res.json())
      .then((data) => setTeamMembers(data))
      .catch((err) => console.error("Team fetch error:", err));
  }, [i18n.language]);

  return (
    <section className="w-full flex flex-col items-center px-4 py-1 mt-8 bg-black text-white font-sans max-w-6xl mx-auto">
      <SEO 
        title={t("pageTitles.team")}
        description="Tiyatro Kumpanyası ekibi - Berlin'de profesyonel tiyatro ve sanat eğitmenleri. Deneyimli ekip üyelerimizle tanışın."
        keywords="tiyatro ekibi, sanat eğitmenleri, berlin tiyatro, theater team, ekip üyeleri"
      />
      {/* Headline */}
      <h1 className="text-3xl font-extrabold underline underline-offset-8 decoration-brand-red-text mb-8 text-center">
        {t("team.title")}
      </h1>

      <Grid container spacing={4} justifyContent="center">
        {teamMembers.map((member, index) => (
          <Grid key={member.id} size={{ xs: 12, sm: 6, md: 3 }}>
            <Box
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              onClick={() => navigate(`/team/${member.id}`)}
              sx={{
                textAlign: "center",
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  aspectRatio: "2/3",
                  borderRadius: "12px",
                  overflow: "hidden",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                }}
              >
                <Box
                  component="img"
                  src={
                    hoveredIndex === index
                      ? member.photo_hover_url
                      : member.photo_main_url
                  }
                  alt={member.name}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    transition: "opacity 0.3s ease-in-out",
                  }}
                />
              </Box>

              <Typography
                variant="subtitle1"
                mt={2}
                sx={{
                  fontWeight: 700,
                  color: "#fff",
                  cursor: "pointer",
                  transition: "color 0.3s ease",
                  "&:hover": {
                    color: "var(--color-brand-red-text)",
                  },
                }}
              >
                {member.name}
              </Typography>

              <Typography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.6)", cursor: "pointer" }}
              >
                {member.role}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
    </section>
  );
};

export default TeamGrid;